import { initSwiper } from '../components/swiper';
import initCartUpsellAtc from '../components/cart-upsell-atc';
import changeCart from './changeCart';
import addToCart from './addToCart';
import changeLineItemOption from './changeLineItemOption';
import { debounce } from 'throttle-debounce';
import { SETTINGS, SELECTORS, CLASSES, MINICART } from '../utils/constants';
import getLocalePrefix from './getLocalePrefix';

const renderMinicart = async () => {
  const minicartLineItemSelector = '[data-minicart-line-item]';
  const minicartRelatedProductsSelector = '[data-minicart-related-products]';
  const minicartSubscribeToggleSelector = '[data-minicart-subscribe-toggle]';
  const minicartSubscribeSelectSelector = '[data-minicart-subscribe-select]';

  const getMinicartData = async () => {
    const localePrefix = getLocalePrefix();
    const url = `${window.location.origin}${localePrefix}/cart?view=minicart`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      return await res.text();
    } catch (e) {
      console.error('Failed to fetch minicart:', e);
      return null;
    }
  };

  const setBtnLoadingState = (button, spinner, loading) => {
    button.classList.toggle(CLASSES.POINTER_EVENTS_NONE, loading);
    button.classList.toggle(CLASSES.OPACITY_50, loading);
    if (spinner) spinner.classList.toggle('hidden', !loading);
  };

  const reinitMinicartUpsell = () => {
    if (!window.theme.swiperObjects) window.theme.swiperObjects = [];

    const sliderEl = document.querySelector('[data-minicart-upsell-swiper]');
    if (!sliderEl) return;

    const existingSwiperIndex = window.theme.swiperObjects.findIndex(swiper => swiper?.el === sliderEl);

    if (existingSwiperIndex !== -1) {
      const existingSwiper = window.theme.swiperObjects[existingSwiperIndex];
      if (existingSwiper && !existingSwiper.destroyed) {
        existingSwiper.destroy(true, true);
      }
      window.theme.swiperObjects.splice(existingSwiperIndex, 1);
    }

    const newSwiper = initSwiper(sliderEl);
    window.theme.swiperObjects.push(newSwiper);
  };

  const setLineItemLoadingState = (el) => {
    el?.classList.add(CLASSES.OPACITY_50);
  };

  const getIdsFromDataset = (datasetValue) => {
    const ids = datasetValue
      ? datasetValue
        .split(',')
        .map(id => +id.trim())
        .filter(Boolean)
      : [];

    return [...new Set(ids)];
  };

  const getIdFromDataset = (datasetValue) => +datasetValue?.trim() || 0;

  const getRecommendationsMarkup = async (recommendationsUrl, productId, sectionId) => {
    const url = new URL(recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', MINICART.RELATED_PRODUCTS_LIMIT);
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.text();
    } catch (error) {
      console.error('Failed to fetch mini cart recommendations:', error);
      return null;
    }
  };

  const renderRelatedProducts = async () => {
    const relatedProductsEls = [...document.querySelectorAll(minicartRelatedProductsSelector)];
    if (!relatedProductsEls.length) return;

    const setRelatedProductsVisibility = (isVisible) => {
      relatedProductsEls.forEach((el) => {
        el.classList.toggle('minicart-related-products--hidden', !isVisible);
        el.setAttribute('aria-hidden', String(!isVisible));
      });
    };

    const relatedProductsContentEls = relatedProductsEls
      .map(el => el.querySelector('[data-minicart-related-content]'))
      .filter(Boolean);

    if (!relatedProductsContentEls.length) return;

    const recommendationsUrl = relatedProductsEls[0].dataset.minicartRecommendationsUrl;
    const recentProductId = getIdFromDataset(relatedProductsEls[0].dataset.minicartRecentProductId);
    const cartProductIds = getIdsFromDataset(relatedProductsEls[0].dataset.minicartCartProductIds);
    const recommendationSeedProductIds = [...new Set([recentProductId, ...cartProductIds].filter(Boolean))];
    const relatedProductsSectionId = MINICART.RELATED_SECTION_ID;

    if (!recommendationsUrl || !recommendationSeedProductIds.length || !relatedProductsSectionId) {
      relatedProductsContentEls.forEach((el) => {
        el.innerHTML = '';
      });
      setRelatedProductsVisibility(false);
      return;
    }

    relatedProductsEls.forEach((el) => {
      el.classList.add('opacity-70', CLASSES.POINTER_EVENTS_NONE);
    });

    try {
      const parser = new DOMParser();
      const seenProductIds = new Set(cartProductIds);
      const cardsMarkup = [];

      for (const productId of recommendationSeedProductIds) {
        if (cardsMarkup.length >= MINICART.RELATED_PRODUCTS_LIMIT) break;

        const htmlText = await getRecommendationsMarkup(recommendationsUrl, productId, relatedProductsSectionId);
        if (!htmlText) continue;

        const doc = parser.parseFromString(htmlText, 'text/html');
        const cards = [...doc.querySelectorAll('[data-minicart-related-card]')];

        cards.forEach((card) => {
          if (cardsMarkup.length >= MINICART.RELATED_PRODUCTS_LIMIT) return;

          const recommendedProductId = +card.dataset.minicartRelatedProductId;
          if (!recommendedProductId || seenProductIds.has(recommendedProductId)) return;

          seenProductIds.add(recommendedProductId);
          cardsMarkup.push(card.outerHTML);
        });
      }

      const markup = cardsMarkup.length
        ? `<div class="flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar lg:flex-col lg:gap-2.5 lg:overflow-visible lg:snap-none">${cardsMarkup.join('')}</div>`
        : '';

      relatedProductsContentEls.forEach((el) => {
        el.innerHTML = markup;
      });
      setRelatedProductsVisibility(Boolean(markup));
    } finally {
      relatedProductsEls.forEach((el) => {
        el.classList.remove('opacity-70', CLASSES.POINTER_EVENTS_NONE);
      });
    }
  };

  const renderRelatedProductsInBackground = async () => {
    try {
      await renderRelatedProducts();
    } catch (error) {
      console.error('Failed to render mini cart recommendations:', error);
    }
  };

  const bindMinicartEvents = (renderMinicartFn) => {
    const minicart = document.querySelector('[data-minicart]');
    if (!minicart) return;

    if (minicart.dataset.eventsBound === 'true') return;
    minicart.dataset.eventsBound = 'true';

    minicart.addEventListener('click', async (evt) => {
      const subscribeToggleBtn = evt.target.closest(minicartSubscribeToggleSelector);
      if (subscribeToggleBtn) {
        evt.preventDefault();
        const lineItem = subscribeToggleBtn.closest(minicartLineItemSelector);
        const subscribeSelect = lineItem?.querySelector(minicartSubscribeSelectSelector);
        const isOpen = subscribeToggleBtn.classList.toggle('open');

        subscribeToggleBtn.setAttribute('aria-pressed', String(isOpen));
        if (subscribeSelect) {
          subscribeSelect.hidden = !isOpen;
        }

        if (!isOpen && subscribeSelect) {
          const hasOneTimeOption = Boolean(subscribeSelect.querySelector('option[value=""]'));
          if (!hasOneTimeOption) {
            subscribeToggleBtn.classList.add('open');
            subscribeToggleBtn.setAttribute('aria-pressed', 'true');
            subscribeSelect.hidden = false;
            return;
          }

          const hasActiveSubscription = subscribeSelect.value !== '';
          if (!hasActiveSubscription) return;

          const lineNumber = +subscribeSelect.dataset.subscribeLine;
          const quantity = +subscribeSelect.dataset.subscribeQuantity || 1;

          if (!lineNumber) return;

          subscribeSelect.disabled = true;
          subscribeToggleBtn.classList.add(CLASSES.POINTER_EVENTS_NONE);
          setLineItemLoadingState(lineItem);

          try {
            await changeLineItemOption({
              lineNumber,
              quantity,
              sellingPlanId: null
            });
            await renderMinicartFn();
          } catch (error) {
            console.error('Failed to switch minicart line item to one-time purchase:', error);
            subscribeSelect.disabled = false;
            subscribeToggleBtn.classList.remove(CLASSES.POINTER_EVENTS_NONE);
            lineItem?.classList.remove(CLASSES.OPACITY_50);
          }
        }

        return;
      }

      const relatedAtcBtn = evt.target.closest('[data-minicart-related-card] button[type="submit"]');
      if (relatedAtcBtn) {
        evt.preventDefault();
        if (relatedAtcBtn.disabled) return;

        const form = relatedAtcBtn.closest('form[data-product-form]');
        if (window.theme?.mobileVariantPicker?.openFromForm?.(form)) return;

        const selectedVariantId = +(form?.elements?.variant?.value || form?.elements?.id?.value || 0);
        if (!selectedVariantId) return;

        const loadingSpinner = relatedAtcBtn.querySelector(SELECTORS.LOADING_SPINNER);
        setBtnLoadingState(relatedAtcBtn, loadingSpinner, true);

        try {
          await addToCart(selectedVariantId, 1);
          await renderMinicartFn();
        } catch (error) {
          console.error('Failed to add related product to cart:', error);
        } finally {
          setBtnLoadingState(relatedAtcBtn, loadingSpinner, false);
        }

        return;
      }

      const btn = evt.target.closest('[data-minicart-remove-item]');
      if (!btn) return;
      evt.preventDefault();
      btn.classList.add(CLASSES.POINTER_EVENTS_NONE);
      const lineItem = btn.closest(minicartLineItemSelector);
      setLineItemLoadingState(lineItem);
      await changeCart(btn.dataset.minicartRemoveItem, 0);
      await renderMinicartFn();
    });

    minicart.addEventListener('change', debounce(SETTINGS.MINICART_DEBOUNCE_DELAY, (evt) => {
      (async () => {
        const subscribeSelect = evt.target.closest(minicartSubscribeSelectSelector);
        if (subscribeSelect) {
          const lineItem = subscribeSelect.closest(minicartLineItemSelector);
          const lineNumber = +subscribeSelect.dataset.subscribeLine;
          const quantity = +subscribeSelect.dataset.subscribeQuantity || 1;
          const sellingPlanId = subscribeSelect.value ? +subscribeSelect.value : null;

          if (!lineNumber) return;

          subscribeSelect.disabled = true;
          setLineItemLoadingState(lineItem);

          try {
            await changeLineItemOption({
              lineNumber,
              quantity,
              sellingPlanId
            });
            await renderMinicartFn();
          } catch (error) {
            console.error('Failed to update mini cart subscription option:', error);
            subscribeSelect.disabled = false;
            lineItem?.classList.remove(CLASSES.OPACITY_50);
          }
          return;
        }

        const input = evt.target.closest('[data-minicart-qty]');
        if (!input) return;

        const lineItem = input.closest(minicartLineItemSelector);
        setLineItemLoadingState(lineItem);
        await changeCart(input.dataset.minicartQty, +input.value);
        await renderMinicartFn();
      })();
    })
    );
  };

  const minicartContent = document.querySelector('[data-minicart-content]');
  const htmlText = await getMinicartData();
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, 'text/html');
  const minicartNewData = doc.querySelector('[data-minicart-render]');
  minicartContent.innerHTML = minicartNewData.innerHTML;

  renderRelatedProductsInBackground();
  reinitMinicartUpsell();
  initCartUpsellAtc(renderMinicart);
  bindMinicartEvents(renderMinicart);
};

export default renderMinicart;
